<template>
  <div>
    <div class="mb-3">
      <label class="form-label small fw-semibold">Nom complet *</label>
      <PrimevueInputText
        class="w-100"
        v-model="form.name"
        required
      />
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <label class="form-label small fw-semibold">Téléphone</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.phone"
          type="tel"
          placeholder="+241..."
          @input="sanitizePhoneField"
        />
      </div>
      <div class="col-md-6">
        <label class="form-label small fw-semibold">WhatsApp</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.whatsapp_phone"
          type="tel"
          placeholder="+241..."
          @input="sanitizeWhatsappField"
        />
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <label class="form-label small fw-semibold">Email</label>
        <PrimevueInputText
          type="email"
          class="w-100"
          v-model="form.email"
          placeholder="client@example.com"
        />
      </div>
    </div>

    <div class="row g-3 mb-3">
      <div class="col-md-6">
        <label class="form-label small fw-semibold">Ville</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.city"
          placeholder="Libreville, ... "
        />
      </div>
      <div class="col-md-6">
        <label class="form-label small fw-semibold">Adresse</label>
        <PrimevueInputText
          class="w-100"
          v-model="form.address"
          placeholder="Adresse complète"
        />
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Plafond dette</label>
      <PrimevueInputNumber
        v-model="form.debt_limit"
        class="w-100"
        mode="decimal"
        :min="0"
        :maxFractionDigits="2"
      />
    </div>

    <div class="border rounded p-3 mb-3">
      <h6 class="mb-3">Pièce d'identité</h6>

      <div class="row g-3 mb-3">
        <div class="col-md-6">
          <label class="form-label small fw-semibold">Type de pièce</label>
          <PrimevueInputText
            id="clientIdentityType"
            class="w-100"
            v-model="form.identity_document_type"
            placeholder="CNI, passeport..."
          />
        </div>
        <div class="col-md-6">
          <label class="form-label small fw-semibold">Numéro de pièce</label>
          <PrimevueInputText
            id="clientIdentityNumber"
            class="w-100"
            v-model="form.identity_document_number"
          />
        </div>
      </div>

      <div class="row g-3">
        <div class="col-md-4">
          <label class="form-label small fw-semibold">Pays d'émission</label>
          <PrimevueInputText
            id="clientIdentityCountry"
            class="w-100"
            v-model="form.identity_document_country"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-semibold">Date d'émission</label>
          <PrimevueInputText
            id="clientIdentityIssuedAt"
            class="w-100"
            v-model="form.identity_document_issued_at"
            type="date"
          />
        </div>
        <div class="col-md-4">
          <label class="form-label small fw-semibold">Date d'expiration</label>
          <PrimevueInputText
            id="clientIdentityExpiresAt"
            class="w-100"
            v-model="form.identity_document_expires_at"
            type="date"
          />
        </div>
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label small fw-semibold">Notes</label>
      <PrimevueTextarea
        class="w-100"
        rows="2"
        v-model="form.notes"
        autoResize
        placeholder="Informations particulières sur ce client"
      />
    </div>

    <div class="d-flex justify-content-end gap-2 mt-3">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="submitting"
        @click="$emit('cancel')"
      >
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        type="button"
        class="btn btn-primary btn-sm"
        :loading="submitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save me-1"></i>
        Enregistrer
      </AppLoadingButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

interface ClientFormData {
  id?: number;
  name: string;
  phone: string | null;
  whatsapp_phone: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  notes: string | null;
  identity_document_type: string | null;
  identity_document_number: string | null;
  identity_document_country: string | null;
  identity_document_issued_at: string | null;
  identity_document_expires_at: string | null;
  debt_limit: number;
}

export default defineComponent({
  name: 'ClientForm',
  props: {
    mode: {
      type: String as PropType<'create' | 'edit'>,
      required: true,
    },
    client: {
      type: Object as PropType<Partial<ClientFormData>>,
      default: () => ({}),
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        id: undefined,
        name: '',
        phone: '',
        whatsapp_phone: '',
        email: '',
        city: '',
        address: '',
        notes: '',
        identity_document_type: '',
        identity_document_number: '',
        identity_document_country: '',
        identity_document_issued_at: '',
        identity_document_expires_at: '',
        debt_limit: 0,
      } as ClientFormData,
    };
  },
  mounted() {
    this.initForm();
  },
  methods: {
    initForm() {
      if (this.mode === 'edit' && this.client && this.client.id) {
        this.form.id = this.client.id;
        this.form.name = this.client.name || '';
        this.form.phone = this.client.phone || '';
        this.form.whatsapp_phone = this.client.whatsapp_phone || '';
        this.form.email = this.client.email || '';
        this.form.city = this.client.city || '';
        this.form.address = this.client.address || '';
        this.form.notes = this.client.notes || '';
        this.form.identity_document_type = this.client.identity_document_type || '';
        this.form.identity_document_number = this.client.identity_document_number || '';
        this.form.identity_document_country = this.client.identity_document_country || '';
        this.form.identity_document_issued_at = this.client.identity_document_issued_at || '';
        this.form.identity_document_expires_at = this.client.identity_document_expires_at || '';
        this.form.debt_limit = Number(this.client.debt_limit || 0);
      } else {
        this.form = {
          id: undefined,
          name: '',
          phone: '',
          whatsapp_phone: '',
          email: '',
          city: '',
          address: '',
          notes: '',
          identity_document_type: '',
          identity_document_number: '',
          identity_document_country: '',
          identity_document_issued_at: '',
          identity_document_expires_at: '',
          debt_limit: 0,
        };
      }
    },

    sanitizePhoneField(event: Event) {
      this.form.phone = sanitizePhoneInput((event.target as HTMLInputElement | null)?.value ?? this.form.phone);
    },

    sanitizeWhatsappField(event: Event) {
      this.form.whatsapp_phone = sanitizePhoneInput(
        (event.target as HTMLInputElement | null)?.value ?? this.form.whatsapp_phone
      );
    },

    submit() {
      if (this.submitting) return;

      if (!this.form.name.trim()) {
        alert('Le nom du client est obligatoire.');
        return;
      }

      if (!isPhoneInputValid(this.form.phone)) {
        alert(phoneInputValidationMessage('téléphone du client'));
        return;
      }

      if (!isPhoneInputValid(this.form.whatsapp_phone)) {
        alert(phoneInputValidationMessage('WhatsApp du client'));
        return;
      }

      const payload = {
        id: this.form.id,
        name: this.form.name.trim(),
        phone: sanitizePhoneInput(this.form.phone) || null,
        whatsapp_phone: sanitizePhoneInput(this.form.whatsapp_phone) || null,
        email: this.form.email || null,
        city: this.form.city || null,
        address: this.form.address || null,
        notes: this.form.notes || null,
        identity_document_type: this.form.identity_document_type || null,
        identity_document_number: this.form.identity_document_number || null,
        identity_document_country: this.form.identity_document_country || null,
        identity_document_issued_at: this.form.identity_document_issued_at || null,
        identity_document_expires_at: this.form.identity_document_expires_at || null,
        debt_limit: Number(this.form.debt_limit || 0),
      };

      this.$emit('save', payload);
    },
  },
});
</script>
